import { createLogger } from "@/lib/logger";
import {
  getTranslationLanguageCodeFromName,
  resolveTranslationLanguageCode,
} from "@/lib/translation-language";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import languages from "../../../data/languages.json";
import { ExerciseData, ExerciseOption } from "../types";
import {
  getInitialLevel,
  getLevelDescription,
  loadLevelProgress,
  saveLevelProgress,
} from "../utils";
import { useAnswerChecker } from "./useAnswerChecker";
import { useExerciseNavigation } from "./useExerciseNavigation";
import { useExerciseQueue } from "./useExerciseQueue";
import { useSpeechSynthesis } from "./useSpeechSynthesis";

const logger = createLogger("useFillInTheGapEngine");

type WordItem = string | ExerciseOption;

const getWordKey = (word: WordItem) => {
  if (typeof word === "string") return word;
  return `${word.text}|${word.transcription || ""}`;
};

interface UseFillInTheGapEngineOptions {
  exerciseContent?: string;
  exerciseOptions?: string[];
  selectedLanguage: string;
  selectedLanguageCode: string;
  initialLanguageLevel: string;
  fromLanguageName?: string;
  translationLanguageCode?: string;
  ttsEnabled: boolean;
  ttsSpeed?: number;
  autoSubmit: boolean;
  playSound: (type: "pop" | "error" | "success") => void;
  onExerciseLoaded?: (exercise: ExerciseData) => void;
  onAnswerResult?: (
    result: string,
    isCorrect: boolean,
    userAnswer: string,
    exerciseSentence: string
  ) => void;
  pendingAnswerSubmission?: string | null;
  onAnswerSubmitted?: () => void;
}

export const useFillInTheGapEngine = ({
  exerciseContent,
  exerciseOptions,
  selectedLanguage,
  selectedLanguageCode,
  initialLanguageLevel,
  fromLanguageName,
  translationLanguageCode,
  ttsEnabled,
  ttsSpeed = 1.0,
  autoSubmit,
  playSound,
  onExerciseLoaded,
  onAnswerResult,
  pendingAnswerSubmission,
  onAnswerSubmitted,
}: UseFillInTheGapEngineOptions) => {
  const [currentSentence, setCurrentSentence] = useState<WordItem[]>([]);
  const [poolWords, setPoolWords] = useState<WordItem[]>([]);
  const [typedAnswer, setTypedAnswer] = useState("");
  const [feedback, setFeedback] = useState("");
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [showNextButton, setShowNextButton] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(() => {
    const savedLevel = loadLevelProgress(selectedLanguage, selectedLanguageCode);
    return savedLevel !== null ? savedLevel : getInitialLevel(initialLanguageLevel);
  });
  const [levelBeforeCheck, setLevelBeforeCheck] = useState(() => {
    const savedLevel = loadLevelProgress(selectedLanguage, selectedLanguageCode);
    return savedLevel !== null ? savedLevel : getInitialLevel(initialLanguageLevel);
  });
  const [refreshLives, setRefreshLives] = useState(3);
  const [consecutiveMistakes, setConsecutiveMistakes] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [exerciseData, setExerciseData] = useState<ExerciseData | null>(null);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [shakeWidget, setShakeWidget] = useState(false);
  const [isProcessingAnswer, setIsProcessingAnswer] = useState(false);
  const [previousLevel, setPreviousLevel] = useState(currentLevel);
  const [discardedWordKeys, setDiscardedWordKeys] = useState<Set<string>>(new Set());

  const autoAdvanceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isGeneratingRef = useRef(false);
  const isProcessingAnswerRef = useRef(false);
  // Keep scoring with the exercise while navigating history in this workspace.
  const exerciseScoresRef = useRef(
    new WeakMap<ExerciseData, { hadWrongAnswer: boolean; answeredCorrectly: boolean }>()
  );
  const exerciseDataRef = useRef(exerciseData);
  const suppressLevelEffectRef = useRef(false);
  const generationRequestIdRef = useRef(0);
  const legacyTranslationCode = getTranslationLanguageCodeFromName(fromLanguageName);
  const resolvedTranslationLanguageCode = resolveTranslationLanguageCode({
    targetLanguageCode: selectedLanguageCode,
    preferredLanguageCode: translationLanguageCode || legacyTranslationCode,
  });
  const previousSelectedLanguageCodeRef = useRef(selectedLanguageCode);
  const previousTranslationLanguageCodeRef = useRef(resolvedTranslationLanguageCode);
  const providedExerciseReplacedRef = useRef(false);
  const handleNavigationNextRef = useRef<() => void>(() => undefined);
  const generationScopeRef = useRef({
    languageCode: selectedLanguageCode,
    level: currentLevel,
    translationLanguageCode: resolvedTranslationLanguageCode,
  });

  generationScopeRef.current = {
    languageCode: selectedLanguageCode,
    level: currentLevel,
    translationLanguageCode: resolvedTranslationLanguageCode,
  };

  const exerciseQueue = useExerciseQueue(fromLanguageName, resolvedTranslationLanguageCode);
  const speechSynthesis = useSpeechSynthesis(exerciseData, selectedLanguageCode, ttsSpeed);
  const answerChecker = useAnswerChecker(currentLevel, consecutiveMistakes);
  const navigation = useExerciseNavigation();
  const getNextExerciseRef = useRef(exerciseQueue.getNextExercise);

  useEffect(() => {
    getNextExerciseRef.current = exerciseQueue.getNextExercise;
  }, [exerciseQueue.getNextExercise]);

  useEffect(() => {
    exerciseDataRef.current = exerciseData;
  }, [exerciseData]);

  const isCurrentGenerationRequest = useCallback(
    (requestId: number, languageCode: string, level: number, translationCode: string) => {
      const scope = generationScopeRef.current;
      return (
        generationRequestIdRef.current === requestId &&
        scope.languageCode === languageCode &&
        scope.level === level &&
        scope.translationLanguageCode === translationCode
      );
    },
    []
  );

  useEffect(() => {
    if (exerciseData && exerciseData.options) {
      const options = exerciseData.options as WordItem[];
      const shuffled = [...options].sort(() => Math.random() - 0.5);
      setPoolWords(shuffled);
      setCurrentSentence([]);
      setDiscardedWordKeys(new Set());
    }
  }, [exerciseData]);

  useEffect(() => {
    const storageKey = "chicky-sentences-history";
    try {
      if (navigation.exerciseHistory.length > 0) {
        localStorage.setItem(storageKey, JSON.stringify(navigation.exerciseHistory));
      }
    } catch (error) {
      logger.error("Failed to save exercise history to localStorage:", error);
    }
  }, [navigation.exerciseHistory]);

  const hasLoadedHistoryRef = useRef(false);
  useEffect(() => {
    if (hasLoadedHistoryRef.current) return;
    hasLoadedHistoryRef.current = true;

    const storageKey = "chicky-sentences-history";
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const history: ExerciseData[] = JSON.parse(stored);
        history.forEach((exercise) => {
          navigation.addToHistory(exercise);
        });
      }
    } catch (error) {
      logger.error("Failed to load exercise history from localStorage:", error);
    }
  }, [navigation.addToHistory]);

  const generateExercise = useCallback(
    async (retryCount = 0) => {
      if (isGeneratingRef.current) {
        logger.info("Generation already in progress, skipping.");
        return;
      }
      isGeneratingRef.current = true;
      const requestId = ++generationRequestIdRef.current;
      const requestedLanguage = selectedLanguage;
      const requestedLanguageCode = selectedLanguageCode;
      const requestedLevel = currentLevel;
      const requestedTranslationCode = resolvedTranslationLanguageCode;
      setIsLoading(true);

      try {
        if (autoAdvanceTimerRef.current) {
          clearTimeout(autoAdvanceTimerRef.current);
          autoAdvanceTimerRef.current = null;
        }

        if (exerciseContent && !providedExerciseReplacedRef.current) {
          const legacyData: ExerciseData = {
            content: exerciseContent,
            correctAnswer: "went",
            options: exerciseOptions || [],
            translation: "Standard exercise",
            language: selectedLanguage,
            languageCode: selectedLanguageCode,
            level: getLevelDescription(currentLevel),
          };
          setExerciseData(legacyData);
          setLevelBeforeCheck(currentLevel);
          if (onExerciseLoaded) {
            onExerciseLoaded(legacyData);
          }
          return;
        }

        const levelString = getLevelDescription(requestedLevel);

        const nextExercise = await getNextExerciseRef.current(
          requestedLanguage,
          requestedLanguageCode,
          levelString
        );

        if (
          !isCurrentGenerationRequest(
            requestId,
            requestedLanguageCode,
            requestedLevel,
            requestedTranslationCode
          )
        ) {
          return;
        }

        if (nextExercise) {
          if (
            nextExercise.languageCode === requestedLanguageCode &&
            nextExercise.language === requestedLanguage &&
            nextExercise.translationLanguageCode === requestedTranslationCode
          ) {
            setExerciseData(nextExercise);
            setLevelBeforeCheck(requestedLevel);
            if (onExerciseLoaded) {
              onExerciseLoaded(nextExercise);
            }

            if (!navigation.isNavigating) {
              navigation.addToHistory(nextExercise);
            }

            setCurrentSentence([]);
            setFeedback("");
            setIsCorrect(null);
            setShowNextButton(false);
            setShowExplanation(false);
            setAttempts(0);
            setTypedAnswer("");
            setShowSuccessOverlay(false);
          } else {
            logger.info("Exercise language mismatch, regenerating...", {
              exerciseLanguage: nextExercise.language,
              exerciseLanguageCode: nextExercise.languageCode,
              expectedLanguage: requestedLanguage,
              expectedLanguageCode: requestedLanguageCode,
              retryCount,
            });

            isGeneratingRef.current = false;

            if (retryCount < 3) {
              generateExercise(retryCount + 1);
            } else {
              setFeedback("Failed to maintain language consistency. Please try again.");
            }
            return;
          }
        } else {
          setFeedback("Failed to load exercise. Please try again.");
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error("Error generating exercise:", errorMessage);
        if (
          isCurrentGenerationRequest(
            requestId,
            requestedLanguageCode,
            requestedLevel,
            requestedTranslationCode
          )
        ) {
          setFeedback(
            `Failed to load exercise: ${
              typeof error === "object" && error !== null && !(error instanceof Error)
                ? JSON.stringify(error)
                : errorMessage
            }`
          );
        }
      } finally {
        // The latest request always owns the busy flag, even if its scope changed while awaiting.
        // A newer request has a different id and will clear the flag itself.
        if (generationRequestIdRef.current === requestId) {
          setIsLoading(false);
          isGeneratingRef.current = false;
        }
      }
    },
    [
      exerciseContent,
      exerciseOptions,
      selectedLanguage,
      selectedLanguageCode,
      currentLevel,
      resolvedTranslationLanguageCode,
      isCurrentGenerationRequest,
      navigation,
      onExerciseLoaded,
    ]
  );

  const clearAutoAdvance = useCallback(() => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
  }, []);

  const handleNext = useCallback(() => {
    clearAutoAdvance();
    isProcessingAnswerRef.current = false;
    setIsProcessingAnswer(false);
    setShowSuccessOverlay(false);

    if (navigation.exerciseHistory.length === 0 && exerciseDataRef.current) {
      navigation.addToHistory(exerciseDataRef.current);
    }

    generateExercise();
  }, [clearAutoAdvance, navigation, generateExercise]);

  const handlePrevious = useCallback(() => {
    if (navigation.canGoToPrevious) {
      const previousExercise = navigation.exerciseHistory[navigation.currentHistoryIndex - 1];
      navigation.goToPreviousExercise();
      setExerciseData(previousExercise);

      setCurrentSentence([]);
      setFeedback("");
      setIsCorrect(null);
      setShowNextButton(false);
      setShowExplanation(false);
      setAttempts(0);
      setTypedAnswer("");

      clearAutoAdvance();
    }
  }, [navigation, clearAutoAdvance]);

  const handleNavigationNext = useCallback(() => {
    navigation.goToNextExercise(() => {
      handleNext();
    });

    if (navigation.canGoToNext) {
      const nextExercise = navigation.exerciseHistory[navigation.currentHistoryIndex + 1];
      setExerciseData(nextExercise);

      setCurrentSentence([]);
      setFeedback("");
      setIsCorrect(null);
      setShowNextButton(false);
      setShowExplanation(false);
      setAttempts(0);
      setTypedAnswer("");

      clearAutoAdvance();
    }
  }, [navigation, handleNext, clearAutoAdvance]);

  handleNavigationNextRef.current = handleNavigationNext;

  const checkAnswer = useCallback(
    async (answerOverride?: string, selectionMeta?: { wordKey: string }) => {
      if (!exerciseData) return;
      if (isProcessingAnswerRef.current) return;

      if (ttsEnabled) {
        speechSynthesis.unlockAudio();
      }

      // Cancel any pending auto-advance when checking a new answer.
      clearAutoAdvance();
      setShowSuccessOverlay(false);
      setShowNextButton(false);

      let answerToCheck = answerOverride;

      if (!answerToCheck) {
        if (currentSentence.length > 0) {
          answerToCheck = currentSentence
            .map((w) => (typeof w === "string" ? w : w.text))
            .join(" ");
        } else {
          answerToCheck = typedAnswer;
        }
      }

      if (!answerToCheck) return;

      isProcessingAnswerRef.current = true;
      setIsProcessingAnswer(true);

      const score = exerciseScoresRef.current.get(exerciseData) ?? {
        hadWrongAnswer: false,
        answeredCorrectly: false,
      };
      const result = await answerChecker.checkAnswer(
        answerToCheck,
        exerciseData,
        !score.hadWrongAnswer && !score.answeredCorrectly
      );
      exerciseScoresRef.current.set(exerciseData, {
        hadWrongAnswer: score.hadWrongAnswer || result.levelChange < 0,
        answeredCorrectly: score.answeredCorrectly || result.isCorrect,
      });

      setLevelBeforeCheck(currentLevel);
      setIsCorrect(result.isCorrect);
      setFeedback(result.feedback);

      if (onAnswerResult) {
        onAnswerResult(
          result.isCorrect ? "User answered correctly" : "User answered incorrectly",
          result.isCorrect,
          answerToCheck,
          exerciseData.content || ""
        );
      }

      if (selectionMeta) {
        if (result.isCorrect) {
          setTypedAnswer(answerToCheck);
        } else {
          setDiscardedWordKeys((prev) => {
            const next = new Set(prev);
            next.add(selectionMeta.wordKey);
            return next;
          });
          setTypedAnswer("");
          setCurrentSentence([]);
        }
      } else if (!result.isCorrect && currentSentence.length > 0) {
        setDiscardedWordKeys((prev) => {
          const next = new Set(prev);
          currentSentence.forEach((word) => {
            next.add(getWordKey(word));
          });
          return next;
        });
        setPoolWords((prev) => {
          const existing = new Set(prev.map(getWordKey));
          const next = [...prev];
          currentSentence.forEach((word) => {
            const key = getWordKey(word);
            if (!existing.has(key)) {
              existing.add(key);
              next.push(word);
            }
          });
          return next;
        });
        setCurrentSentence([]);
      }

      if (result.isCorrect) {
        setShowNextButton(true);
        playSound("success");
        setShowSuccessOverlay(true);
        setTimeout(() => setShowSuccessOverlay(false), 2000);

        if (result.shouldAdvanceLevel) {
          suppressLevelEffectRef.current = true;
          setCurrentLevel((prev) => {
            const nextLevel = Math.min(100, prev + result.levelChange);
            saveLevelProgress(selectedLanguage, selectedLanguageCode, nextLevel);
            return nextLevel;
          });
        }
        setConsecutiveMistakes(0);

        if (ttsEnabled) {
          speechSynthesis.speakSentence();
        }

        autoAdvanceTimerRef.current = setTimeout(() => {
          handleNavigationNextRef.current();
        }, 3000);
      } else {
        playSound("error");
        setShakeWidget(true);
        setTimeout(() => setShakeWidget(false), 500);

        // Show explanation immediately on mistake
        setShowExplanation(true);

        if (result.levelChange < 0) {
          suppressLevelEffectRef.current = true;
          setCurrentLevel((prev) => {
            const nextLevel = Math.max(1, prev + result.levelChange);
            saveLevelProgress(selectedLanguage, selectedLanguageCode, nextLevel);
            return nextLevel;
          });
          setConsecutiveMistakes((prev) => prev + 1);
          setRefreshLives((prev) => Math.max(0, prev - 1));
        }

        setAttempts((prev) => prev + 1);
      }

      isProcessingAnswerRef.current = false;
      setIsProcessingAnswer(false);
    },
    [
      exerciseData,
      currentSentence,
      typedAnswer,
      answerChecker,
      onAnswerResult,
      speechSynthesis,
      ttsEnabled,
      playSound,
      selectedLanguage,
      selectedLanguageCode,
      currentLevel,
    ]
  );

  const handleRefresh = useCallback(() => {
    if (refreshLives > 0) {
      setRefreshLives((prev) => prev - 1);
      generateExercise();
    } else {
      setFeedback("No refresh lives left!");
    }
  }, [refreshLives, generateExercise]);

  const handleWordClick = useCallback(
    (word: WordItem, fromSentence: boolean, index: number) => {
      playSound("pop");

      if (fromSentence) {
        const newSentence = [...currentSentence];
        newSentence.splice(index, 1);
        setCurrentSentence(newSentence);
        setPoolWords([...poolWords, word]);
      } else {
        if (autoSubmit) {
          const wordKey = getWordKey(word);
          if (discardedWordKeys.has(wordKey)) {
            return;
          }
          setTypedAnswer("");
          setCurrentSentence([]);
          const sentenceText = typeof word === "string" ? word : word.text;
          checkAnswer(sentenceText, { wordKey });
          return;
        }

        const newPool = [...poolWords];
        newPool.splice(index, 1);
        setPoolWords(newPool);
        const newSentence = [...currentSentence, word];
        setCurrentSentence(newSentence);
      }
    },
    [playSound, currentSentence, poolWords, autoSubmit, checkAnswer, discardedWordKeys]
  );

  const handleTypedAnswerChange = useCallback(
    (value: string) => {
      setTypedAnswer(value);
      if (value && currentSentence.length > 0) {
        setPoolWords([...poolWords, ...currentSentence]);
        setCurrentSentence([]);
      }
    },
    [currentSentence, poolWords]
  );

  useEffect(() => {
    generateExercise();
  }, []);

  useEffect(() => {
    const languageChanged = previousSelectedLanguageCodeRef.current !== selectedLanguageCode;
    previousSelectedLanguageCodeRef.current = selectedLanguageCode;
    const translationChanged =
      previousTranslationLanguageCodeRef.current !== resolvedTranslationLanguageCode;
    previousTranslationLanguageCodeRef.current = resolvedTranslationLanguageCode;
    if (translationChanged) providedExerciseReplacedRef.current = true;

    if (!exerciseContent || providedExerciseReplacedRef.current) {
      if (suppressLevelEffectRef.current) {
        suppressLevelEffectRef.current = false;
        setPreviousLevel(currentLevel);
        if (!languageChanged && !translationChanged) return;
      }

      const isManualLevelChange =
        Math.abs(currentLevel - previousLevel) > 1 ||
        (currentLevel !== previousLevel && !autoAdvanceTimerRef.current && !showNextButton);

      const shouldUpdateExercise =
        languageChanged ||
        translationChanged ||
        (Boolean(exerciseData) && !isProcessingAnswer && isManualLevelChange);

      if (shouldUpdateExercise) {
        const newLanguageName = languageChanged
          ? Object.entries(languages).find(([, code]) => code === selectedLanguageCode)?.[0] ||
            selectedLanguage
          : selectedLanguage;

        // A support-language change starts fresh practice without rewriting saved translations.
        if (!translationChanged || languageChanged || isManualLevelChange) {
          navigation.resetNavigation();
        }
        clearAutoAdvance();
        isProcessingAnswerRef.current = false;
        setIsProcessingAnswer(false);
        setFeedback("");
        setIsCorrect(null);
        setShowNextButton(false);
        setShowExplanation(false);
        setCurrentSentence([]);
        setAttempts(0);
        setTypedAnswer("");

        const requestedLanguageCode = selectedLanguageCode;
        const requestedLevel = currentLevel;
        const requestedTranslationCode = resolvedTranslationLanguageCode;
        const currentLevelString = getLevelDescription(requestedLevel);
        const requestId = ++generationRequestIdRef.current;
        isGeneratingRef.current = true;

        const handleLanguageOrLevelChange = async () => {
          setIsLoading(true);
          try {
            const firstExercise = await exerciseQueue.getNextExercise(
              newLanguageName,
              requestedLanguageCode,
              currentLevelString
            );

            if (
              isCurrentGenerationRequest(
                requestId,
                requestedLanguageCode,
                requestedLevel,
                requestedTranslationCode
              )
            ) {
              if (firstExercise) {
                setExerciseData(firstExercise);
                setLevelBeforeCheck(requestedLevel);
                if (translationChanged && !languageChanged && !isManualLevelChange) {
                  navigation.addToHistory(firstExercise);
                }
                onExerciseLoaded?.(firstExercise);
              } else {
                setExerciseData(null);
                setFeedback("Failed to load exercise. Please try again.");
              }
            }
          } catch (error) {
            logger.error("Error generating exercises for language/level change:", error);
            if (
              isCurrentGenerationRequest(
                requestId,
                requestedLanguageCode,
                requestedLevel,
                requestedTranslationCode
              )
            ) {
              setFeedback("Failed to load exercise for the new language/level. Please try again.");
            }
          } finally {
            if (generationRequestIdRef.current === requestId) {
              setIsLoading(false);
              isGeneratingRef.current = false;
            }
          }
        };
        handleLanguageOrLevelChange();

        setPreviousLevel(currentLevel);
        saveLevelProgress(selectedLanguage, selectedLanguageCode, currentLevel);
      }
    }
  }, [selectedLanguageCode, currentLevel, resolvedTranslationLanguageCode]);

  useEffect(() => {
    if (pendingAnswerSubmission && exerciseData && !isProcessingAnswer) {
      logger.info("Processing pending answer submission:", pendingAnswerSubmission);

      const words = pendingAnswerSubmission.toLowerCase().split(" ");
      const matchedWords: string[] = [];

      poolWords.forEach((word) => {
        const text = typeof word === "string" ? word : word.text;
        if (words.some((w) => text.toLowerCase().includes(w) || w.includes(text.toLowerCase()))) {
          matchedWords.push(typeof word === "string" ? word : word.text);
        }
      });

      if (matchedWords.length > 0) {
        const matchedObjects: WordItem[] = [];
        poolWords.forEach((word) => {
          const text = typeof word === "string" ? word : word.text;
          if (matchedWords.includes(text)) {
            matchedObjects.push(word);
          }
        });

        setCurrentSentence(matchedObjects);
        setPoolWords(poolWords.filter((w) => !matchedObjects.includes(w)));
      }

      if (onAnswerSubmitted) {
        onAnswerSubmitted();
      }
    }
  }, [pendingAnswerSubmission, exerciseData, isProcessingAnswer, onAnswerSubmitted, poolWords]);

  useEffect(() => {
    return () => {
      // Note: speechSynthesis handles its own unmount cleanup in useSpeechSynthesis
      clearAutoAdvance();
    };
  }, [clearAutoAdvance]);

  useEffect(() => {
    if (!ttsEnabled) {
      speechSynthesis.stopSpeech("tts_disabled_by_user");
    }
  }, [ttsEnabled, speechSynthesis]);

  return useMemo(
    () => ({
      exerciseQueue,
      navigation,
      exerciseData,
      isLoading,
      feedback,
      isCorrect,
      showNextButton,
      showExplanation,
      setShowExplanation,
      currentLevel,
      setCurrentLevel,
      levelBeforeCheck,
      refreshLives,
      attempts,
      showSuccessOverlay,
      setShowSuccessOverlay,
      shakeWidget,
      setShakeWidget,
      currentSentence,
      poolWords,
      discardedWordKeys,
      getWordKey,
      typedAnswer,
      handleTypedAnswerChange,
      handleWordClick,
      checkAnswer,
      handleNext,
      handlePrevious,
      handleNavigationNext,
      handleRefresh,
      generateExercise,
      clearAutoAdvance,
      isProcessingAnswer,
      speakSentence: speechSynthesis.speakSentence,
      isSpeaking: speechSynthesis.isSpeaking,
    }),
    [
      exerciseQueue,
      navigation,
      exerciseData,
      isLoading,
      feedback,
      isCorrect,
      showNextButton,
      showExplanation,
      currentLevel,
      levelBeforeCheck,
      refreshLives,
      attempts,
      showSuccessOverlay,
      shakeWidget,
      currentSentence,
      poolWords,
      discardedWordKeys,
      typedAnswer,
      handleTypedAnswerChange,
      handleWordClick,
      checkAnswer,
      handleNext,
      handlePrevious,
      handleNavigationNext,
      handleRefresh,
      generateExercise,
      clearAutoAdvance,
      isProcessingAnswer,
      speechSynthesis.speakSentence,
      speechSynthesis.isSpeaking,
    ]
  );
};

export default useFillInTheGapEngine;
